import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { AtomsToastVariant } from '@opendoor/atoms/toast';
import { useAtomsToastManager } from '@opendoor/atoms/toast';

type ToastType = 'error' | 'info' | 'success';

export type ShowToastFn = (options: ToastOptions) => void;

export type ToastOptions = {
  /**
   * The type of the toast - affects styling
   */
  type: ToastType;

  /**
   * The message to display
   */
  message: string;

  /**
   * Used for tracking impressions
   */
  analyticsName: string;

  /**
   * Delay in ms before the toast becomes visible
   *
   * Default 0 ms
   */
  openDelayMs?: number;

  /**
   * Auto-close duration in ms
   *
   * Default 3,000 ms
   */
  autoCloseMs?: number;
};

const DEFAULT_AUTO_CLOSE_MS = 3_000;

const VARIANT_BY_TYPE: Record<ToastType, AtomsToastVariant> = {
  error: 'error',
  info: 'info',
  success: 'success',
};

/**
 * Given defaults `D`, computes what `showToast` still requires:
 * - Fields not in `D` keep their original optionality from `ToastOptions`
 * - Fields in `D` become optional overrides
 */
type ShowToastArg<D extends Partial<ToastOptions>> = Omit<ToastOptions, keyof D> &
  Partial<Pick<ToastOptions, Extract<keyof D, keyof ToastOptions>>>;

/**
 * Imperative wrapper over the atoms Toast manager exposing `showToast` /
 * `hideToast` / `ToastElement`. Toasts render in the app-level `<Toasty>`
 * viewport (mounted in `CakeAtomsProvider`), so `ToastElement` is a no-op kept
 * for call-site compatibility.
 *
 * Accepts optional defaults so that `showToast` only requires the fields not
 * already provided:
 *
 * ```ts
 * const { showToast } = useToast({ type: 'error', analyticsName: 'my-toast' });
 * showToast({ message: 'Something went wrong' });
 * ```
 *
 * Default values are overridden when new values are passed to `showToast`.
 */
export const useToast = <D extends Partial<ToastOptions>>(defaults?: D) => {
  // The manager identity changes each render; read it through a ref so the
  // returned callbacks stay stable (call sites pass `showToast` into effect deps).
  // The ref is read at call time (post-commit), so syncing it in an effect rather
  // than during render is safe.
  const manager = useAtomsToastManager();
  const managerRef = useRef(manager);
  useEffect(() => {
    managerRef.current = manager;
  });

  const defaultsRef = useRef(defaults);
  const delayRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    return () => clearTimeout(delayRef.current);
  }, []);

  const showToast = useCallback(
    (
      ...args: {} extends ShowToastArg<D> ? [options?: ShowToastArg<D>] : [options: ShowToastArg<D>]
    ) => {
      const merged = { ...defaultsRef.current, ...args[0] } as ToastOptions;
      clearTimeout(delayRef.current);

      const open = () => {
        lastIdRef.current = managerRef.current.add({
          title: merged.message,
          variant: VARIANT_BY_TYPE[merged.type],
          timeout: merged.autoCloseMs ?? DEFAULT_AUTO_CLOSE_MS,
        });
      };

      if (merged.openDelayMs) {
        delayRef.current = setTimeout(open, merged.openDelayMs);
      } else {
        open();
      }
    },
    [],
  );

  const hideToast = useCallback(() => {
    clearTimeout(delayRef.current);
    if (lastIdRef.current) {
      managerRef.current.close(lastIdRef.current);
    }
  }, []);

  return useMemo(() => ({ showToast, hideToast, ToastElement: null }), [showToast, hideToast]);
};
